import { Activity, CheckCircle, Database, Globe, Loader2, Server, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { ServiceDependencyGraph } from "../components/health/ServiceDependencyGraph";
import type { HealthStatus, PlatformStats, ServiceHealth } from "../types/platform";

type HealthData = {
  health: HealthStatus | null;
  platformHealth: { services: ServiceHealth } | null;
  stats: PlatformStats | null;
};

const SERVICE_TARGETS = [
  { id: "lexserver", name: "LexServer", icon: Server, description: "Platform backend (Hono)", check: "self" },
  { id: "mongodb", name: "MongoDB", icon: Database, description: "Document store", check: "dependency" },
  { id: "redis", name: "Redis", icon: Database, description: "Cache & routing", check: "dependency" },
  { id: "lexruntime", name: "LexRuntime", icon: Activity, description: "Per-user agent runtime (Docker)", check: "external" },
  { id: "clerk", name: "Clerk", icon: Globe, description: "User identity & auth", check: "external" },
] as const;

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) { return `${d}d ${h}h ${m}m`; }
  if (h > 0) { return `${h}h ${m}m`; }
  return `${m}m`;
}

/**
 * Probe the lexruntime container via its harn-service health endpoint.
 *
 * **Dev-only.** Uses the Vite-proxied `/harn-direct/health` path
 * which only exists in the dev server (see lexportal/vite.config.ts
 * `/harn-direct` → `127.0.0.1:3210`). In production builds that
 * proxy doesn't exist, so the fetch would 404 and we'd report
 * `false` — a false-negative scarier than reporting "unknown". A
 * real per-user cross-runtime probe is a Phase 10.5 / Phase 14 v2
 * concern and ships when production lexruntime fan-out lands.
 *
 * Returns `null` in production (renders as "Unknown"), `true`/`false`
 * in dev based on the probe result.
 */
async function probeRuntime(): Promise<boolean | null> {
  if (!import.meta.env.DEV) return null;
  try {
    const res = await fetch("/harn-direct/health", { method: "GET" });
    return res.ok;
  } catch {
    return false;
  }
}

export function PlatformHealth() {
  const api = usePlatformApi();
  const [data, setData] = useState<HealthData>({ health: null, platformHealth: null, stats: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runtimeHealthy, setRuntimeHealthy] = useState<boolean | null>(null);

  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const t0 = performance.now();
      const [health, platformHealth, stats, runtimeOk] = await Promise.all([
        api.getHealth().catch(() => null),
        api.getPlatformHealth().catch(() => null),
        api.getStats().catch(() => null),
        probeRuntime(),
      ]);
      setLatencyMs(Math.round(performance.now() - t0));
      setLastChecked(new Date());

      setData({
        health: health,
        platformHealth: platformHealth?.data ?? null,
        stats: stats?.data ?? null,
      });
      setRuntimeHealthy(runtimeOk);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load health data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  const mongoOk = data.platformHealth?.services.mongodb === "connected";
  const redisOk = data.platformHealth?.services.redis === "connected";
  const lexserverOk = data.health?.status === "ok";
  // Clerk has no live health probe today (lexserver doesn't ping the
  // Clerk API). Reflect *configuration* presence instead — if a
  // Clerk publishable key is set on the LexPortal build, sign-in
  // works and the integration is considered "ok". The card carries a
  // tooltip explaining this is config-only, not a live probe.
  const clerkConfigured = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

  function getServiceStatus(id: string): "ok" | "degraded" | "unknown" {
    switch (id) {
      case "lexserver": return lexserverOk ? "ok" : data.health ? "degraded" : "unknown";
      case "mongodb": return mongoOk ? "ok" : data.platformHealth ? "degraded" : "unknown";
      case "redis": return redisOk ? "ok" : data.platformHealth ? "degraded" : "unknown";
      case "lexruntime": return runtimeHealthy === true ? "ok" : runtimeHealthy === false ? "degraded" : "unknown";
      case "clerk": return clerkConfigured ? "ok" : "unknown";
      default: return "unknown";
    }
  }

  /**
   * Per-service tooltip shown on the status card. Used to surface
   * caveats — e.g. Clerk is config-only, runtime probe goes through
   * the direct port.
   */
  function getServiceTooltip(id: string): string | undefined {
    if (id === "clerk") {
      return clerkConfigured
        ? "Clerk publishable key is configured. This is a config check, not a live probe — sign-in flow surfaces real Clerk failures."
        : "Clerk is not configured for this build (VITE_CLERK_PUBLISHABLE_KEY not set). Local-dev mode bypasses Clerk via dev-org JWT.";
    }
    if (id === "lexruntime") {
      return import.meta.env.DEV
        ? "Probed via /harn-direct/health (dev-only Vite proxy → harn-service :3210)."
        : "Per-user runtime probe not yet implemented. Use the runtime selector for per-instance status; cross-runtime aggregate ships with Phase 10.5.";
    }
    return undefined;
  }

  const statusColor = (s: "ok" | "degraded" | "unknown") =>
    s === "ok" ? "text-green-500" : s === "degraded" ? "text-red-500" : "text-slate-400";

  const StatusIcon = ({ status }: { status: "ok" | "degraded" | "unknown" }) =>
    status === "ok" ? <CheckCircle className={`h-5 w-5 ${statusColor(status)}`} /> :
    status === "degraded" ? <XCircle className={`h-5 w-5 ${statusColor(status)}`} /> :
    <div className="h-5 w-5 rounded-full border-2 border-slate-300 dark:border-slate-600" />;

  const okCount = SERVICE_TARGETS.filter((s) => getServiceStatus(s.id) === "ok").length;
  const overallStatus = okCount === SERVICE_TARGETS.length ? "All systems operational" :
    okCount > 0 ? "Partial degradation" : "Systems unavailable";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Platform Health</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {overallStatus} &middot; {okCount}/{SERVICE_TARGETS.length} services healthy
            {latencyMs != null && <> &middot; API latency: <span className="font-mono">{latencyMs}ms</span></>}
            {lastChecked && <> &middot; checked {lastChecked.toLocaleTimeString()}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
          <button type="button" onClick={() => load()} disabled={loading}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">{error}</div>
      )}

      {/* Service Status Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICE_TARGETS.map((svc) => {
          const status = getServiceStatus(svc.id);
          const Icon = svc.icon;
          const tooltip = getServiceTooltip(svc.id);
          return (
            <Card key={svc.id} title={tooltip}>
              <CardContent className="flex items-start gap-3 pt-5">
                <StatusIcon status={status} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="font-medium">{svc.name}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{svc.description}</p>
                  <p className={`mt-1 text-xs font-medium ${statusColor(status)}`}>
                    {status === "ok" ? "Healthy" : status === "degraded" ? "Unhealthy" : "Unknown"}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* §14 — Service dependency graph (topology view of the same
          status signal the cards show; no extra probe). */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Service Dependency Graph
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center overflow-x-auto">
          <ServiceDependencyGraph statusOf={getServiceStatus} />
        </CardContent>
      </Card>

      {/* Server Details */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* LexServer Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">LexServer</CardTitle>
          </CardHeader>
          <CardContent>
            {data.health ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className={`font-medium ${data.health.status === "ok" ? "text-green-500" : "text-red-500"}`}>
                    {data.health.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Uptime</span>
                  <span className="font-mono text-xs">{formatUptime(data.health.uptime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">MongoDB</span>
                  <span className={mongoOk ? "text-green-500" : "text-red-500"}>
                    {data.health.dependencies.mongodb}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Redis</span>
                  <span className={redisOk ? "text-green-500" : "text-red-500"}>
                    {data.health.dependencies.redis}
                  </span>
                </div>
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-slate-400">Unable to reach LexServer</p>
            )}
          </CardContent>
        </Card>

        {/* Platform Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Platform Stats</CardTitle>
          </CardHeader>
          <CardContent>
            {data.stats ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Active Orgs</span>
                  <span className="font-semibold">{data.stats.orgs.active}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Suspended Orgs</span>
                  <span>{data.stats.orgs.suspended}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Provisioning</span>
                  <span>{data.stats.orgs.provisioning}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Users</span>
                  <span className="font-semibold">{data.stats.totalUsers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Active Tasks</span>
                  <span>{data.stats.activeTasks}</span>
                </div>
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-slate-400">Loading...</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Response Latency & Task Queue */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">API Response Latency</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums">{latencyMs ?? "—"}</span>
              <span className="text-sm text-muted-foreground">ms</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Round-trip to lexserver (health + stats + services)
            </p>
            <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className={`h-1.5 rounded-full transition-all ${
                  latencyMs == null ? "w-0" :
                  latencyMs < 200 ? "bg-green-500 w-1/4" :
                  latencyMs < 500 ? "bg-yellow-500 w-1/2" :
                  latencyMs < 1000 ? "bg-orange-500 w-3/4" : "bg-red-500 w-full"
                }`}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Task Queue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums">{data.stats?.activeTasks ?? "—"}</span>
              <span className="text-sm text-muted-foreground">active tasks</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              BullMQ provisioning & lifecycle jobs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Platform Scale</CardTitle>
          </CardHeader>
          <CardContent>
            {data.stats ? (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Organizations</span>
                  <span className="font-semibold">{data.stats.orgs.active + data.stats.orgs.suspended + data.stats.orgs.provisioning}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Users</span>
                  <span className="font-semibold">{data.stats.totalUsers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Terminated Orgs</span>
                  <span className="text-slate-400">{data.stats.orgs.terminated}</span>
                </div>
              </div>
            ) : (
              <p className="py-2 text-center text-sm text-slate-400">Loading...</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
